import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CellRenderer } from './cell-renderer';
import { DefaultHeader, DraggableColumnHeader } from './header-renderer';
import {
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { useColumnReorder } from './use-column-reorder';
import {
  CellComponentProps,
  Column,
  ColumnLayout,
  DataGridProps,
  DataGridRow,
  FocusedCell,
  RowComponentProps,
  ScrollPosition,
} from './types';
import { useColumnResize } from './use-column-resize';
import { useContainerSize } from './use-container-size';
import { useVirtualization } from './use-virtualization';
import { Loading } from '@cotera/client/app/components';
import { useIntersectionObserver } from './use-intersection-observer';

const DefaultRow = ({
  children,
  selected,
  onClick,
  style,
}: RowComponentProps) => (
  <div
    style={style}
    className={`flex border-b outline-none ring-0 border-gray-200 ${
      selected ? 'bg-blue-50' : 'hover:bg-gray-50'
    }`}
    onClick={onClick}
    role="row"
    tabIndex={-1}
  >
    {children}
  </div>
);

// Custom hook to detect visibility in a scrollable container
const useIntersectionObserverHeader = (
  columns: Watchable<Column[]>,
  containerRef: React.RefObject<HTMLElement>,
  threshold: number = 0.1 // How much of the element needs to be visible (0.1 = 10%)
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const observedElements = useRef<Set<HTMLElement>>(new Set());

  // Function to register an item for observation
  const registerItem = useCallback(
    (id: string, element: HTMLElement | null) => {
      if (!element) {
        // Clean up if element is removed
        const oldElement = itemRefs.current.get(id);
        if (oldElement && observerRef.current) {
          observerRef.current.unobserve(oldElement);
          observedElements.current.delete(oldElement);
        }
        itemRefs.current.delete(id);
        return;
      }

      const oldElement = itemRefs.current.get(id);
      if (oldElement && oldElement !== element) {
        // Element changed, unobserve the old one
        if (observerRef.current) {
          observerRef.current.unobserve(oldElement);
          observedElements.current.delete(oldElement);
        }
      }

      itemRefs.current.set(id, element);

      if (observerRef.current && !observedElements.current.has(element)) {
        observerRef.current.observe(element);
        observedElements.current.add(element);
      }
    },
    []
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Capture the current ref values to use in cleanup
    const currentObserver = observerRef.current;
    const currentObservedElements = observedElements.current;

    // Clean up existing observer
    if (currentObserver) {
      currentObserver.disconnect();
      currentObservedElements.clear();
    }

    // Create intersection observer with the container as root
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Only update columns that actually changed
        const currentColumns = columns.snapshot();
        const updates = new Map<string, boolean>();

        entries.forEach((entry) => {
          const columnKey = entry.target.getAttribute('data-item-id');
          if (columnKey) {
            updates.set(columnKey, entry.isIntersecting);
          }
        });

        // Only update if there are actual changes
        const hasChanges = Array.from(updates.entries()).some(
          ([key, isIntersecting]) => {
            const col = currentColumns.find((c) => c.key === key);
            return col && col.inView !== isIntersecting;
          }
        );

        if (hasChanges) {
          columns.set(
            currentColumns.map((col) => {
              const newInView = updates.get(col.key);
              return newInView !== undefined
                ? { ...col, inView: newInView }
                : col;
            })
          );
        }
      },
      {
        root: containerRef.current, // Use the scrollable container as root
        rootMargin: '0px',
        threshold: threshold,
      }
    );

    // Observe all currently registered items
    itemRefs.current.forEach((element) => {
      if (observerRef.current && !observedElements.current.has(element)) {
        observerRef.current.observe(element);
        observedElements.current.add(element);
      }
    });

    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
        currentObservedElements.clear();
      }
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, threshold]); // Remove columns from dependencies

  return { registerItem };
};

type Context = {
  dataContainerRef: React.RefObject<HTMLDivElement> | null;
  scrollRef: React.RefObject<HTMLDivElement> | null;
  RowComponent: React.ComponentType<RowComponentProps>;
  handleCellClick: (
    rowData: any,
    column: Column,
    rowIndex: number,
    columnIndex: number,
    e: React.MouseEvent
  ) => void;
  columns: Column[];
  columnLayout: ColumnLayout;
  model: {
    focusedCell: Watchable<
      (FocusedCell & { columnKey: string; rowData: any }) | null
    >;
    selectedRows: Watchable<Set<number>>;
    selectionAnchor: Watchable<number | null>;
  };
  onSelectCell?: (row: number, col: number, rowData: any) => void;
  onLoadMore?: () => void;
  hasMore: boolean;
  rowHeight: number;
  headerHeight: number;
  containerSize: {
    width: number;
    height: number;
  };
  scrollPosition: ScrollPosition;
  scrollToCell: (row: number, col: number) => void;
};
const context = createContext<Context | null>(null);

const DataGridProvider = ({
  children,
  ...rest
}: { children: React.ReactNode } & Context) => {
  return <context.Provider value={rest}>{children}</context.Provider>;
};

const useDataGridContext = () => {
  const ctx = useContext(context)!;
  if (ctx === null) {
    throw new Error('DataGridProvider not found');
  }
  return ctx;
};

export const DefaultRowNumberHeader = ({
  column,
  headerHeight,
  children,
}: {
  column: Column & { x: number; width: number };
  headerHeight: number;
  children?: React.ReactNode;
}) => (
  <div
    key={column.key}
    className="flex-shrink-0 border-r border-border flex justify-center"
    style={{
      width: column.width,
      height: headerHeight,
      position: 'sticky',
      left: column.x,
      zIndex: 30,
    }}
  >
    {children ?? (
      <span className="text-sm font-medium text-foreground mt-2.5">#</span>
    )}
  </div>
);

// Create the hard-coded row number column
const rowNumberColumn: Column & { x: number; width: number } = {
  key: '__row_number__',
  title: '#',
  type: 'number',
  width: 40,
  pinned: 'left',
  status: 'visible',
  icon: 'hash',
  t: 'assumption',
  x: 0,
  inView: true,
};

const DataGrid = <T extends DataGridRow = DataGridRow>({
  data = [],
  model,
  rowHeight = 48,
  headerHeight: headerHeightWatchable,
  onLoadMore,
  hasMore = false,
  CellComponent = CellRenderer,
  HeaderComponent = DefaultHeader,
  RowComponent = DefaultRow,
  className = '',
  minColumnWidth = 100,
  maxColumnWidth = 500,
  onSelectCell,
  body = (
    <DataGridBody
      CellComponent={CellComponent}
      data={data}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
    />
  ),
  onFocus,
  onBlur,
  topBar: TopBar,
  RowNumberHeaderComponent = DefaultRowNumberHeader,
}: DataGridProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dataContainerRef = useRef<HTMLDivElement>(null);
  const { containerSize, containerRef } = useContainerSize();
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    x: 0,
    y: 0,
  });
  const allColumns = useWatchableValue(model.columns);
  const columns = allColumns.filter((col) => col.status === 'visible');
  const headerHeight = useWatchableValue(headerHeightWatchable);

  const pinnedLeft = columns
    .filter((col) => col.pinned === 'left')
    .map((col) => col.key);
  const pinnedRight = columns
    .filter((col) => col.pinned === 'right')
    .map((col) => col.key);

  // Column resize functionality
  const { resizingColumn, handleResizeStart } = useColumnResize(
    allColumns,
    (newColumns) => {
      model.columns.set(newColumns);
    },
    minColumnWidth,
    maxColumnWidth
  );

  // Column reordering functionality
  const { moveColumn } = useColumnReorder(
    columns,
    pinnedLeft,
    pinnedRight,
    (newColumns) => {
      model.columns.set(newColumns);
    },
    (newColumns) => {
      model.columns.set(
        model.columns
          .snapshot()
          .map((col) =>
            col.key === newColumns[0] ? { ...col, pinned: 'left' } : col
          )
      );
    },
    (newColumns) => {
      model.columns.set(
        model.columns
          .snapshot()
          .map((col) =>
            col.key === newColumns[0] ? { ...col, pinned: 'right' } : col
          )
      );
    }
  );

  // Calculate column positions and widths
  const columnLayout: ColumnLayout = useMemo(() => {
    const cols = columns.map((x) => ({
      ...x,
      pinned: x.pinned ?? null,
    }));
    const makePinned = (pinned: 'left' | 'right' | null) => {
      let currentX = 0;
      const newColumns = cols
        .filter((col) => col.pinned === pinned)
        .map((col) => {
          const width = col.width ?? 200;
          const x = currentX;
          currentX += width;
          return { ...col, x, width };
        });

      return {
        columns: newColumns,
        width: currentX,
      };
    };

    const leftPinned = makePinned('left');
    const rightPinned = makePinned('right');
    const regular = makePinned(null);

    // Add row number column to the beginning of left pinned columns
    const leftPinnedWithRowNumber = {
      columns: [
        rowNumberColumn,
        ...leftPinned.columns.map((col) => ({
          ...col,
          x: col.x + rowNumberColumn.width,
        })),
      ],
      width: leftPinned.width + rowNumberColumn.width,
    };

    return {
      leftPinned: leftPinnedWithRowNumber.columns,
      regular: regular.columns,
      rightPinned: rightPinned.columns,
      leftPinnedWidth: leftPinnedWithRowNumber.width,
      regularWidth: regular.width,
      rightPinnedWidth: rightPinned.width,
      totalWidth:
        leftPinnedWithRowNumber.width + rightPinned.width + regular.width,
    };
  }, [columns]);

  // Handle scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollLeft, scrollTop } = target;
    setScrollPosition({ x: scrollLeft, y: scrollTop });
  }, []);

  const scrollToCell = useCallback(
    (row: number, col: number) => {
      if (!scrollRef.current) {
        return;
      }

      const scrollContainer = scrollRef.current;
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate row position
      const rowTop = row * rowHeight;
      const rowBottom = rowTop + rowHeight;
      const visibleTop = scrollContainer.scrollTop;
      const visibleBottom = visibleTop + containerRect.height - headerHeight;

      // Scroll vertically if needed
      if (rowTop < visibleTop) {
        scrollContainer.scrollTop = rowTop - rowHeight; // Add some padding
      } else if (rowBottom > visibleBottom) {
        scrollContainer.scrollTop =
          rowBottom - containerRect.height + headerHeight + rowHeight;
      }

      // Calculate column position (only for regular columns, pinned columns are always visible)
      const regularColumns = columnLayout.regular;
      if (col < regularColumns.length) {
        const column = regularColumns[col];
        if (column) {
          const colLeft = column.x;
          const colRight = colLeft + column.width;
          const visibleLeft = scrollContainer.scrollLeft;
          const visibleRight =
            visibleLeft +
            containerRect.width -
            columnLayout.leftPinnedWidth -
            columnLayout.rightPinnedWidth;

          // Scroll horizontally if needed
          if (colLeft < visibleLeft) {
            scrollContainer.scrollLeft = colLeft - 20; // Add some padding
          } else if (colRight > visibleRight) {
            scrollContainer.scrollLeft =
              colRight -
              containerRect.width +
              columnLayout.leftPinnedWidth +
              columnLayout.rightPinnedWidth +
              20;
          }
        }
      }
    },
    [rowHeight, headerHeight, columnLayout]
  );

  // Handle cell click
  const handleCellClick = useCallback(
    (
      rowData: T,
      column: Column,
      rowIndex: number,
      columnIndex: number,
      e: React.MouseEvent
    ) => {
      e.stopPropagation(); // Prevent row selection if cell selection is primary

      const isShiftClick = e.shiftKey;
      const isModifierClick = e.ctrlKey || e.metaKey;

      if (isShiftClick) {
        // Range selection: select from anchor to clicked row
        const anchor = model.selectionAnchor.snapshot() ?? rowIndex;
        const start = Math.min(anchor, rowIndex);
        const end = Math.max(anchor, rowIndex);

        const newSelection = new Set<number>();
        for (let i = start; i <= end; i++) {
          newSelection.add(i);
        }
        model.selectedRows.set(newSelection);
        // Don't update anchor on shift-click
      } else if (isModifierClick) {
        // Toggle this row in selection
        const currentSelection = model.selectedRows.snapshot();
        const newSelection = new Set(currentSelection);
        if (newSelection.has(rowIndex)) {
          newSelection.delete(rowIndex);
        } else {
          newSelection.add(rowIndex);
        }
        model.selectedRows.set(newSelection);
        model.selectionAnchor.set(rowIndex);
      } else {
        // Clear and select single row
        model.selectedRows.set(new Set([rowIndex]));
        model.selectionAnchor.set(rowIndex);
      }

      // Update focused cell
      model.focusedCell.set({
        row: rowIndex,
        col: columnIndex,
        columnKey: column.key,
        rowData,
      });

      // Auto-scroll to the clicked cell
      setTimeout(() => scrollToCell(rowIndex, columnIndex), 0);
    },
    [scrollToCell, model.focusedCell, model.selectedRows, model.selectionAnchor]
  );

  // Pin column functions
  const pinColumn = useCallback(
    (columnKey: string, side: 'left' | 'right') => {
      model.columns.set(
        columns.map((col) =>
          col.key === columnKey ? { ...col, pinned: side } : col
        )
      );
    },
    [columns, model.columns]
  );

  const unpinColumn = useCallback(
    (columnKey: string) => {
      model.columns.set(
        columns.map((col) =>
          col.key === columnKey ? { ...col, pinned: null } : col
        )
      );
    },
    [columns, model.columns]
  );

  const { registerItem } = useIntersectionObserverHeader(
    model.columns,
    scrollRef,
    0.5
  );

  // Render header row with sticky positioning
  const renderHeaderRow = (
    columnGroup: Array<Column & { x: number; width: number }>,
    isPinned = false
  ) => {
    const groupType = isPinned
      ? columnGroup[0]?.pinned === 'left'
        ? 'leftPinned'
        : 'rightPinned'
      : 'regular';

    return (
      <div className="flex">
        {columnGroup.map((column, index) => {
          // Special handling for row number column header
          if (column.key === '__row_number__') {
            return (
              <RowNumberHeaderComponent
                key={column.key}
                column={column}
                headerHeight={headerHeight}
              />
            );
          }

          return (
            <DraggableColumnHeader
              height={headerHeight}
              HeaderComponent={HeaderComponent}
              key={column.key}
              column={column}
              index={index}
              group={groupType}
              onMoveColumn={moveColumn}
              onPinColumn={pinColumn}
              onUnpinColumn={unpinColumn}
              isLastInGroup={index === columnGroup.length - 1}
              onResizeStart={handleResizeStart}
              resizingColumn={resizingColumn}
              registerRef={registerItem}
            />
          );
        })}
      </div>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        onFocus={onFocus}
        onBlur={onBlur}
        ref={containerRef}
        className={`flex flex-col relative overflow-hidden ${className}`}
        style={{
          height: '100%',
          width:
            columnLayout.totalWidth < containerSize.width ? '100%' : undefined,
          overscrollBehaviorX: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        role="grid"
      >
        {TopBar && <TopBar />}
        {/* Single scroll container with sticky positioning */}
        <div
          ref={scrollRef}
          className="h-full overflow-auto"
          onScroll={handleScroll}
        >
          <div
            ref={dataContainerRef}
            style={{
              width: Math.max(columnLayout.totalWidth, containerSize.width),
              height: containerSize.height - headerHeight,
              position: 'relative',
            }}
          >
            <DataGridProvider
              model={model}
              onSelectCell={onSelectCell}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              rowHeight={rowHeight}
              headerHeight={headerHeight}
              dataContainerRef={dataContainerRef}
              scrollRef={scrollRef}
              handleCellClick={handleCellClick}
              RowComponent={RowComponent}
              columns={columns}
              containerSize={containerSize}
              scrollPosition={scrollPosition}
              columnLayout={columnLayout}
              scrollToCell={scrollToCell}
            >
              {/* Headers with sticky positioning */}
              <div className="sticky top-0 z-20 bg-white">
                <div className="flex border-b border-gray-200">
                  {/* Left pinned headers */}
                  {columnLayout.leftPinned.length > 0 && (
                    <div
                      className="flex-shrink-0 bg-white"
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 30,
                      }}
                    >
                      {renderHeaderRow(columnLayout.leftPinned, true)}
                    </div>
                  )}

                  {/* Regular headers */}
                  <div className="flex-shrink-0">
                    {renderHeaderRow(columnLayout.regular)}
                  </div>

                  {/* Right pinned headers */}
                  {columnLayout.rightPinned.length > 0 && (
                    <div
                      className="flex-shrink-0 bg-white shadow-md"
                      style={{
                        position: 'sticky',
                        right: 0,
                        zIndex: 30,
                      }}
                    >
                      {renderHeaderRow(columnLayout.rightPinned, true)}
                    </div>
                  )}
                </div>
              </div>

              {/* Data rows with sticky positioning */}
              <div className="relative">{body}</div>
            </DataGridProvider>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

// Updated Row component to handle sticky positioning
type RowProps<T extends DataGridRow = DataGridRow> = {
  model: {
    focusedCell: Watchable<
      (FocusedCell & { columnKey: string; rowData: T }) | null
    >;
    selectedRows: Watchable<Set<number>>;
  };
  rowData: T;
  columns: Column[];
  columnLayout: ColumnLayout;
  CellComponent: React.ComponentType<CellComponentProps<T>>;
  handleCellClick: (
    rowData: T,
    column: Column,
    rowIndex: number,
    columnIndex: number,
    e: React.MouseEvent
  ) => void;
  RowComponent: React.ComponentType<RowComponentProps>;
  rowHeight: number;
  actualRowIndex: number;
};

// Special cell renderer for row numbers
const RowNumberCell: React.FC<{ rowIndex: number }> = ({ rowIndex }) => (
  <div className="flex items-center justify-center h-full text-sm text-foreground font-medium">
    {rowIndex + 1}
  </div>
);

const Row = <T extends DataGridRow = DataGridRow>({
  model,
  rowData,
  columns,
  columnLayout,
  CellComponent,
  handleCellClick,
  RowComponent,
  rowHeight,
  actualRowIndex,
}: RowProps<T>) => {
  const focusedCell = useWatchableValue(model.focusedCell);
  const selectedRows = useWatchableValue(model.selectedRows);
  const isRowSelected = selectedRows.has(actualRowIndex);

  // Prevent text selection when shift-clicking for multi-select
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.shiftKey) {
      e.preventDefault();
    }
  }, []);

  return (
    <RowComponent selected={isRowSelected} style={{ height: rowHeight }}>
      <div className="flex" onMouseDown={handleMouseDown}>
        {/* Left pinned cells */}
        {columnLayout.leftPinned.map((column, colIndex) => {
          const globalColumnIndex = columns.findIndex(
            (c) => c.key === column.key
          );
          const isCellFocused =
            focusedCell?.row === actualRowIndex &&
            focusedCell?.col === globalColumnIndex;

          // Special handling for row number column
          if (column.key === '__row_number__') {
            return (
              <div
                key={column.key}
                className="flex-shrink-0 border-r bg-white border-border"
                style={{
                  width: column.width,
                  position: 'sticky',
                  left: column.x,
                  zIndex: 10,
                }}
              >
                <RowNumberCell rowIndex={actualRowIndex} />
              </div>
            );
          }

          return (
            <div
              key={column.key}
              className={`flex-shrink-0 border-r border-gray-200 cursor-pointer ${
                isCellFocused
                  ? 'ring-2 ring-blue-500 ring-inset bg-blue-100'
                  : ''
              }`}
              style={{
                width: column.width,
                position: 'sticky',
                left: column.x,
                zIndex: 10,
                backgroundColor: 'white',
              }}
              onClick={(e) =>
                handleCellClick(
                  rowData,
                  column,
                  actualRowIndex,
                  globalColumnIndex,
                  e
                )
              }
            >
              <CellComponent
                value={rowData[column.key]}
                rowData={rowData}
                column={column}
                rowIndex={actualRowIndex}
                columnIndex={colIndex}
              />
            </div>
          );
        })}

        {/* Regular cells */}
        {columnLayout.regular.map((column, colIndex) => {
          const globalColumnIndex = columns.findIndex(
            (c) => c.key === column.key
          );
          const isCellFocused =
            focusedCell?.row === actualRowIndex &&
            focusedCell?.col === globalColumnIndex;

          return (
            <div
              key={column.key}
              className={`flex-shrink-0 border-r border-gray-200 cursor-pointer ${
                isCellFocused
                  ? 'ring-2 ring-blue-500 ring-inset bg-blue-100'
                  : ''
              }`}
              style={{ width: column.width }}
              onClick={(e) =>
                handleCellClick(
                  rowData,
                  column,
                  actualRowIndex,
                  globalColumnIndex,
                  e
                )
              }
            >
              <CellComponent
                value={rowData[column.key]}
                rowData={rowData}
                column={column}
                rowIndex={actualRowIndex}
                columnIndex={colIndex}
              />
            </div>
          );
        })}

        {/* Right pinned cells */}
        {columnLayout.rightPinned.map((column, colIndex) => {
          const globalColumnIndex = columns.findIndex(
            (c) => c.key === column.key
          );
          const isCellFocused =
            focusedCell?.row === actualRowIndex &&
            focusedCell?.col === globalColumnIndex;

          return (
            <div
              key={column.key}
              className={`flex-shrink-0 border-r border-gray-200 cursor-pointer ${
                isCellFocused
                  ? 'ring-2 ring-blue-500 ring-inset bg-blue-100'
                  : ''
              }`}
              style={{
                width: column.width,
                position: 'sticky',
                right: columnLayout.rightPinnedWidth - column.x - column.width,
                zIndex: 10,
                backgroundColor: 'white',
              }}
              onClick={(e) =>
                handleCellClick(
                  rowData,
                  column,
                  actualRowIndex,
                  globalColumnIndex,
                  e
                )
              }
            >
              <CellComponent
                value={rowData[column.key]}
                rowData={rowData}
                column={column}
                rowIndex={actualRowIndex}
                columnIndex={colIndex}
              />
            </div>
          );
        })}
      </div>
    </RowComponent>
  );
};

type DataGridBodyProps<T extends DataGridRow = DataGridRow> = {
  data: T[];
  hasMore: boolean;
  onLoadMore?: () => void | Promise<void>;
  CellComponent?: React.ComponentType<CellComponentProps<T>>;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const DataGridBody = <T extends DataGridRow = DataGridRow>({
  data = [],
  onLoadMore,
  hasMore,
  CellComponent = CellRenderer,
  onFocus,
  onBlur,
}: DataGridBodyProps<T>) => {
  const [intersectionRef, loadMoreIntersectionVisible] =
    useIntersectionObserver({ threshold: 0.1 });

  const {
    dataContainerRef,
    scrollRef,
    model,
    columns,
    columnLayout,
    handleCellClick,
    RowComponent,
    rowHeight,
    containerSize,
    headerHeight,
    scrollPosition,
    onSelectCell,
    scrollToCell,
  } = useDataGridContext();

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastLoadMoreTime, setLastLoadMoreTime] = useState(0);

  const virtualization = useVirtualization(
    data.length,
    rowHeight,
    containerSize.height - headerHeight,
    scrollPosition.y,
    8
  );

  const visibleRows = data.slice(
    virtualization.startIndex,
    virtualization.endIndex + 1
  );

  //update the height of the data container to the total height of the rows
  useEffect(() => {
    if (dataContainerRef?.current) {
      if (virtualization.totalHeight > 0) {
        // Ensure the container height is at least the viewport height minus header height
        // to prevent headers from scrolling off screen when there are few rows
        const minHeight = containerSize.height;
        const contentHeight = virtualization.totalHeight + headerHeight;
        const finalHeight = Math.max(minHeight, contentHeight);

        dataContainerRef.current.style.height = finalHeight + 'px';
      }
    }
  }, [
    data.length,
    virtualization.totalHeight,
    headerHeight,
    dataContainerRef,
    containerSize.height,
  ]);

  useEffect(() => {
    if (loadMoreIntersectionVisible && hasMore && !isLoadingMore) {
      const now = Date.now();
      if (now - lastLoadMoreTime < 1000) {
        return; // Prevent rapid triggers
      }

      void (async () => {
        setIsLoadingMore(true);
        setLastLoadMoreTime(now);
        await onLoadMore?.();
        setIsLoadingMore(false);
      })();
    }
  }, [
    loadMoreIntersectionVisible,
    hasMore,
    onLoadMore,
    isLoadingMore,
    lastLoadMoreTime,
  ]);

  // Fallback: trigger load more when user scrolls near bottom
  useEffect(() => {
    if (!hasMore || isLoadingMore || !onLoadMore) {
      return;
    }

    const container = scrollRef?.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Trigger when user is within 20% of the bottom
      if (scrollPercentage > 0.8) {
        const now = Date.now();
        if (now - lastLoadMoreTime < 1000) {
          return; // Prevent rapid triggers
        }

        void (async () => {
          setIsLoadingMore(true);
          setLastLoadMoreTime(now);
          await onLoadMore();
          setIsLoadingMore(false);
        })();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, onLoadMore, scrollRef, lastLoadMoreTime]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const handFocusedCell = model.focusedCell.snapshot();
      const { row, col } = model.focusedCell.snapshot() ?? { row: 0, col: 0 };
      let newRow = row;
      let newCol = col;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newRow = Math.max(0, row - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newRow = Math.min(data.length - 1, row + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newCol = Math.max(0, col - 1);
          // Skip row number column if we're moving left
          if (newCol === 0 && columns[0]?.key === '__row_number__') {
            newCol = 1;
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          newCol = Math.min(columns.length - 1, col + 1);
          // Skip row number column if we're moving right
          if (newCol === 0 && columns[0]?.key === '__row_number__') {
            newCol = 1;
          }
          break;
        case ' ': {
          if (handFocusedCell) {
            e.preventDefault();
            onFocus?.();
            onSelectCell?.(
              handFocusedCell.row,
              handFocusedCell.col,
              data[handFocusedCell.row]!
            );
          }
          break;
        }
        case 'Enter':
        case 'Home':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+Home: Go to first cell
            newRow = 0;
            newCol = 0;
            // Skip row number column
            if (columns[0]?.key === '__row_number__') {
              newCol = 1;
            }
          } else {
            // Home: Go to first column of current row
            newCol = 0;
            // Skip row number column
            if (columns[0]?.key === '__row_number__') {
              newCol = 1;
            }
          }
          break;
        case 'End':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+End: Go to last cell
            newRow = data.length - 1;
            newCol = columns.length - 1;
          } else {
            // End: Go to last column of current row
            newCol = columns.length - 1;
          }
          break;
        case 'PageUp':
          e.preventDefault();
          newRow = Math.max(
            0,
            row - Math.floor((containerSize.height - headerHeight) / rowHeight)
          );
          break;
        case 'PageDown':
          e.preventDefault();
          newRow = Math.min(
            data.length - 1,
            row + Math.floor((containerSize.height - headerHeight) / rowHeight)
          );
          break;
        default:
          return;
      }

      model.focusedCell.set({
        row: newRow,
        col: newCol,
        columnKey: columns[newCol]!.key,
        rowData: data[newRow]!,
      });

      // Auto-scroll to the new focused cell
      setTimeout(() => scrollToCell(newRow, newCol), 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      columns.length,
      containerSize.height,
      headerHeight,
      rowHeight,
      scrollToCell,
    ]
  );

  return (
    <>
      <div className="flex flex-col h-full">
        <div
          className="outline-none ring-0"
          style={{ paddingTop: virtualization.offsetY }}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          {visibleRows.map((rowData, rowIndex) => {
            const actualRowIndex = virtualization.startIndex + rowIndex;

            return (
              <Row
                key={actualRowIndex}
                model={model}
                rowData={rowData}
                columns={columns}
                columnLayout={columnLayout}
                CellComponent={CellComponent}
                handleCellClick={handleCellClick}
                RowComponent={RowComponent}
                rowHeight={rowHeight}
                actualRowIndex={actualRowIndex}
              />
            );
          })}
        </div>
        {/** intersection observer */}
        {hasMore && (
          <div
            ref={intersectionRef}
            className="h-4 w-full"
            style={{ minHeight: '16px' }}
          />
        )}
        {/* Loading indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-4 relative h-12">
            <div className="sticky inset-0 left-[50%] bottom-0 flex items-center gap-2">
              <Loading.Spinner variant="sm" className="translate-x-[-50%]" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export { DataGrid };
